// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll, { FancyScrollRefProps } from "@electrowire/App/Components/Common/FancyScroll";
import { TippyPopupStyle } from "@electrowire/App/Components/Tippy/Style";
import TippyPopup from "@electrowire/App/Components/Tippy/TippyPopup";
import { xHeaderUpdate } from "@electrowire/App/Handlers/XNG/Header";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import SearchHistory, { SearchHistoryStyles } from "@electrowire/Overrides/Widgets/Header/SearchHistory";
import BarcodeScanner from "@gcg/App/Components/BarcodeScanner/BarcodeScanner";
import { isBannerScannerAvailable } from "@gcg/App/Components/BarcodeScanner/BarcodeScannerHelpers";
import { addSearchHistory, getSearchHistory ,SearchHistoryItem,} from "@gcg/App/Services/AutocompleteService";
import { color, styledScrollbar, xClassNames, xScreen } from "@gcg/XDL/Utilities";
import { mergeWithCss } from "@gcg/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Search from "@insite/mobius/Icons/Search";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { Instance as TippyInstance } from "tippy.js";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    // XNG-Changes
    xHeaderUpdate,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    // XNG-Changes
    tippyInstance?: TippyInstance;
    enterKey: boolean;
}

// XNG-Changes
const DOWN_KEY = "ArrowDown";
const UP_KEY = "ArrowUp";
const ENTER_KEY = "Enter";

const sharedSearchInputStyle: { autocompleteCategoriesStyles?: AutocompleteCategoriesStyles } = {
    autocompleteCategoriesStyles: {
        headerText: {
            size: 16,
            weight: "bold",
            transform: "uppercase",
            css: css`
                letter-spacing: 0;
                line-height: 19px;
                padding: 12px 20px;
                background: ${color("common.ui12", 0.35)};
                margin-bottom: 12px;
                & ~ a,
                & ~ button,
                & ~ ${TypographyStyle}:not([data-test-selector="autocompleteBrands_Header"]) {
                    margin-bottom: 15px;
                    padding: 0 20px;
                    &:last-child {
                        margin-bottom: 0;
                    }
                }

                & ~ ${TypographyStyle} + [data-test-selector="autocompleteBrands_Header"] {
                    margin-top: 20px;
                }
            `,
        },
        link: {
            color: "text.main",
            typographyProps: {
                size: 15,
                weight: "normal",
                css: css`
                    letter-spacing: 0;
                    line-height: 24px;
                `,
            },
        },
        focusedCategoryItemText: {
            size: 15,
            weight: "bold",
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 24px;
            `,
        },
        focusedLink: {
            color: "text.main",
            typographyProps: {
                size: 15,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 24px;
                `,
            },
        },
        categoryItemText: {
            color: "common.ui13",
            size: 15,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 24px;
            `,
        },
    },
};

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    inputWrapper?: InjectableCss;
    autocompleteColumnProductWrapper?: InjectableCss;
    tippyPopup?: TippyPopupStyle;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            size: 25,
            src: Search,
            color: "primary",
        },
        cssOverrides: {
            formInputWrapper: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")}
                margin-top: 0 !important;
            `,
        },
    },
    tippyPopup: {
        main: {
            css: css`
                &.tippy-box > .tippy-content {
                    padding: 0;
                }
            `,
        },
    },
    inputWrapper: {
        css: css`
            line-height: 0; // to fix extra spacing on firefox
            position: relative;

            &&&& {
                input {
                    padding-right: 42px;
                    height: 42px;
                    ${xScreen("gt", "md")} {
                        height: 48px;
                    }
                    &::placeholder {
                        color: ${color("common.ui5")};
                        line-height: 20px;
                    }
                }
                &.bannerScanner-available input {
                    padding-right: 86px;
                }

                ${FormFieldClickable} {
                    padding: 0;
                    ${xScreen("gt", "md")} {
                        top: 3px;
                        right: 4px;
                        width: 40px;
                        height: 40px;
                        padding: 12px;
                    }

                    ${FormFieldIcon} {
                        top: 8px;
                        right: 8px;
                        width: 25px;
                        height: 25px;
                        padding: 0;

                        svg {
                            width: 100%;
                            height: 100%;
                        }
                    }
                }
            }
        `,
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            max-width: calc(100vw - 50px);
            ${xScreen("lte", "sm")} {
                flex-direction: column-reverse;
            }
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 400px;
            min-width: 400px;
            min-height: 400px;
            max-height: 540px;
            padding-bottom: 20px;
            padding-left: 10px;
            position: relative;
            &:after {
                content: "";
                background: ${color("common.ui6")};
                width: 1px;
                height: 100%;
                position: absolute;
                right: 0;
                top: 0;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
                min-width: initial;
                display: none;
            }
            ${styledScrollbar(4)}
        `,
    },
    autocompleteColumnProductWrapper: {
        css: css`
            display: flex;
            width: 750px;
            padding: 20px 20px;
            max-height: 540px;

            flex-direction: row;
            flex-wrap: wrap;
            gap: 15px 38px;
            & > div {
                width: auto;
                flex: 1 1 calc(50% - 19px);
            }

            ${xScreen("lte", "md")} {
                width: 100%;
            }
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
            ${styledScrollbar(4)}
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedCategoryItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedCategoryItemText,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
        categoryItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.categoryItemText,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedBrandItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedCategoryItemText,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
        brandItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.categoryItemText,
        },
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private parentMenu = React.createRef<HTMLUListElement>();
    private fancyScrollRef = React.createRef<FancyScrollRefProps>();
    private inputWrapperRef = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeWithCss(styles, props.extendedStyles);

        this.state = {
            query: "",
            focusableAutocompleteItems: [],
            isLoading: false,
            tippyInstance: undefined,
            enterKey: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });

                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "" || this.state.isLoading) {
            return;
        }
        
        if (this.state.enterKey && !this.state.autoCompleteModel) {
            return;
        }

        const hasProductErpNumberMatchingQuery = this.state.autoCompleteModel?.products?.some(
            (product) => product.erpNumber?.toLowerCase() === this.state.query?.toLowerCase()
        );

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
            if (this.props.searchHistoryEnabled) {
                addSearchHistory(this.state.query, this.props.searchHistoryLimit, false, this.state.autoCompleteModel?.products[0].url);
                return;
            }
        } else if (hasProductErpNumberMatchingQuery){
            const product = this.state.autoCompleteModel?.products?.find(
                (product) => product.erpNumber?.toLowerCase() === this.state.query?.toLowerCase()
            );
            if (product){
                this.goToUrl(product!.url);
                if (this.props.searchHistoryEnabled) {
                    addSearchHistory(this.state.query, this.props.searchHistoryLimit, false, product.url);
                    return;
                }
            } else {
                this.goToUrl(`/${this.props.searchPath}?query=${this.state.query}`, false);
            }
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: result,
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    // <XNG-Changes>
                    this.updatePopoverVisibility(result);
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);

        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.show();
    };
    
    closeAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.hide();
    };
    
    // XNG-Changes: Check if popeover is opened
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                (brands && brands.length > 0) ||
                (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    // XNG-Changes
    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
                    positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                this.setState({ enterKey: true });
                if (this.state.focusedAutocompleteItem) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    // XNG-Changes
                    (event.target as any).blur();
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }
        
        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }
        
        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        
        return (
            <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                    <StyledWrapper {...this.styles.autocompleteColumnWrapper} className="autocomplete-column-wrapper">
                        <AutocompleteCategories
                            categories={categories}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                        />
                        <AutocompleteBrands
                            brands={brands}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteBrandsStyles}
                        />
                        <AutocompleteContent
                            content={content}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteContentStyles}
                        />
                    </StyledWrapper>
                )}
                {products && products.length > 0 && (
                    <FancyScroll refElement={this.parentMenu} ref={this.fancyScrollRef}>
                        <StyledWrapper
                            {...this.styles.autocompleteColumnProductWrapper}
                            className="autocomplete-column-wrapper"
                            ref={this.parentMenu as any}
                        >
                            <AutocompleteProducts
                                products={products}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                // doSearch={this.doSearch}
                                extendedStyles={this.styles.autocompleteProductsStyles}
                                {...{query:this.state.query} as any}
                            />
                        </StyledWrapper>
                    </FancyScroll>
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        if (!this.state.query && this.showAutocomplete) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                    extendedStyles={{
                        wrapper: {
                            css: css`
                                padding: 20px;
                                width: ${this.inputWrapperRef.current?.getBoundingClientRect().width || 300}px;
                                ${xScreen("lte", "xs")} {
                                    width: 100%;
                                }
                            `,
                        },
                    }}
                />
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        const { xHeaderUpdate } = this.props;

        const searchInput = (
            <StyledWrapper
                ref={this.inputWrapperRef}
                {...styles.inputWrapper}
                className={xClassNames.append(isBannerScannerAvailable() ? "bannerScanner-available" : "")}
            >
                <Hidden above="md">
                    <BarcodeScanner></BarcodeScanner>
                </Hidden>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handlePopoverKeyDown}
                    onFocus={this.handleInputFocus}
                    placeholder={translate("Search by Product Number, Name, Brand or Keyword")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    aria-multiline="false"
                    aria-required="false"
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    maxLength={100}
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <TippyPopup
                    onCreate={instance => {
                        this.setState({ tippyInstance: instance });
                    }}
                    onShow={instance => {
                        xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_search` });
                        this.fancyScrollRef?.current?.updateScrollable();
                        this.setState({ tippyInstance: instance });
                    }}
                    onHidden={instance => {
                        xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_search` });
                    }}
                    content={<>{this.renderSearchAutoCompleteContent()}</>}
                    trigger="manual"
                    offset={[10, 0]}
                    duration={[null, null]}
                    arrow={false}
                    maxWidth="none"
                    placement="bottom-start"
                    mobileMode="left-side"
                    showCloseButton={false}
                    extendedStyles={styles.tippyPopup}
                >
                    {searchInput}
                </TippyPopup>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
